<section class="digital-twin-container">
  <div class="content-box">
    <h1>AquaTech Digital Twin: AI for Aquaculture Farmers</h1>
    <p>
      Harness the power of artificial intelligence to optimize your aquaculture
      operations, increase yields, and ensure sustainability.
    </p>
    <div class="btn-group">
      <a routerLink="/contact" href="/contact" class="btn-primary">Request a Demo</a>
     <!-- <a class="btn-outline" (click)="getStart()" >See it in Action</a>-->
    </div>
  </div>

  <div class="image-box">
    <img
      src="assets/images/digitalTwin/digitalTwin.png"
      alt="AI Fish Sensor Visualization"
    />
  </div>
</section>
<section class="challenge-section py-5" id="get-start">
  <div class="container">
    <div class="text-center mb-5">
      <h2 class="fw-bold heading_color">The Challenge: Why AquaTech Digital Twin Exists</h2>
      <p class="section-subtitle">
        Shrimp farmers face invisible threats beneath the water surface. These key issues impact yield, costs, and peace of mind.
      </p>
    </div>

    <div class="challenge-grid">
      <!-- Card 1 -->
      <div class="challenge-card">
        <div class="challenge-icon water"><i class="bi bi-droplet-fill"></i></div>
        <h4 class="heading_color">Unseen Water Stress</h4>
        <p>Fluctuating DO, pH, and temperature silently reduce shrimp growth before farmers can react.</p>
      </div>

      <!-- Card 2 -->
      <div class="challenge-card">
        <div class="challenge-icon alert"><i class="bi bi-exclamation-triangle-fill"></i></div>
        <h4 class="heading_color">Late Disease Detection</h4>
        <p>WSSV and EMS appear only after visible damage, leading to severe pond losses.</p>
      </div>

      <!-- Card 3 -->
      <div class="challenge-card">
        <div class="challenge-icon feed"><i class="bi bi-water"></i></div>
        <h4 class="heading_color">Inefficient Feeding</h4>
        <p>Overfeeding raises costs, degrades water, and harms pond ecosystems.</p>
      </div>

      <!-- Card 4 -->
      <div class="challenge-card">
        <div class="challenge-icon weather"><i class="bi bi-cloud-rain-fill"></i></div>
        <h4 class="heading_color">Unpredictable Weather</h4>
        <p>Sudden rain or heatwaves disrupt pond balance, increasing stress and mortality.</p>
      </div>
    </div>
  </div>
</section>


<section class="differentiation-section py-5 text-light">
  <div class="container">
    <div class="text-center mb-5">
      <h2 class="fw-bold mb-3">
        Differentiation / What Makes This Digital Twin Different
      </h2>
      <p>
        Our Digital Twin goes beyond monitoring it
        <strong>understands</strong>, <strong>predicts</strong>, and
        <strong>advises</strong> for smarter aquaculture decisions.
      </p>
    </div>

    <div class="row gy-4">
      <!-- Real-time 3D Visualization -->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-cpu-fill text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">Real-time 3D Visualization</h5>
          <p class="text-secondary small">
            A digital replica of your pond with live sensor feeds showing water
            conditions in real time.
          </p>
        </div>
      </div>

      <!-- AI-powered Prediction -->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-graph-up-arrow text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">AI-powered Prediction</h5>
          <p class="text-secondary small">
            Detects early signs of disease or poor water quality using advanced
            AI models.
          </p>
        </div>
      </div>

      <!-- Simulation Engine -->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-diagram-3-fill text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">Simulation Engine</h5>
          <p class="text-secondary small">
            Test “what-if” scenarios to predict outcomes before applying them in
            real ponds.
          </p>
        </div>
      </div>

      <!-- Smart Recommendations -->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-lightbulb-fill text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">Smart Recommendations</h5>
          <p class="text-secondary small">
            Get AI-driven advice for feeding, aeration, and biosecurity based on
            real-time data.
          </p>
        </div>
      </div>

      <!-- Scalable & Connected -->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-cloud-check-fill text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">Scalable & Connected</h5>
          <p class="text-secondary small">
            Manage multiple ponds or farms seamlessly from a single connected
            dashboard.
          </p>
        </div>
      </div>

      <!-- Continuous Learning System-->
      <div class="col-md-4 col-lg-4">
        <div class="card h-100 border-0 shadow-sm p-4 text-center">
          <i class="bi bi-cloud-check-fill text-info fs-1 mb-3"></i>
          <h5 class="fw-semibold mb-2 heading_color">Continuous Learning System</h5>
          <p class="text-secondary small">
            The DT evolves with every production cycle  improving predictions, adapting to local pond behavior, and getting smarter over time.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="core-gaps py-5">
  <div class="container">
    <h2 class="text-center mb-5 fw-bold heading_color">Core Gaps Solved</h2>

    <!-- Gap 1 -->
    <div class="card gap-card mb-4">
      <div class="row g-0 align-items-center">
        <!-- Problem -->
        <div class="col-md-4 text-center p-4 border-end">
          <i class="bi bi-exclamation-triangle-fill icon text-danger fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Problem</h5>
          <p>Shrimp diseases (WSSV, EMS) spread fast and are detected only after
            visible damage.</p>
        </div>
        <!-- Arrow -->
      <div class="col-md-1 text-center d-none d-md-block">
        <i class="bi bi-arrow-right moving-arrow fs-3 heading_color"></i>
      </div>

        <!-- Solution -->
        <div class="col-md-3 text-center p-4 border-end">
          <i class="bi bi-check-circle-fill icon text-success fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Solution</h5>
          <p>The DT uses live data and AI models to detect early risks and issue
            preventive alerts.</p>
        </div>

        <!-- Arrow -->
      <div class="col-md-1 text-center d-none d-md-block">
        <i class="bi bi-arrow-right moving-arrow fs-3 heading_color"></i>
      </div>

        <!-- Outcome -->
        <div class="col-md-3 text-center p-4">
          <i class="bi bi-graph-up-arrow icon heading_color fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Outcome</h5>
          <p>Farmers act days earlier  reducing mortality and protecting nearby ponds.</p>
        </div>
      </div>
    </div>

    <!-- Gap 2 -->
    <div class="card gap-card">
      <div class="row g-0 align-items-center">
        <!-- Problem -->
        <div class="col-md-4 text-center p-4 border-end">
          <i class="bi bi-exclamation-triangle-fill icon text-danger fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Problem</h5>
          <p>Sudden rain, heatwaves, and salinity shifts cause pond stress.</p>
        </div>

              <!-- Arrow -->
       <div class="col-md-1 text-center d-none d-md-block">
        <i class="bi bi-arrow-right moving-arrow fs-3 heading_color"></i>
      </div>


        <!-- Solution -->
        <div class="col-md-3 text-center p-4 border-end">
          <i class="bi bi-check-circle-fill icon text-success fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Solution</h5>
          <p>The DT uses weather forecasts and sensor data to suggest preemptive measures.</p>
        </div>

      <!-- Arrow -->
      <div class="col-md-1 text-center d-none d-md-block">
        <i class="bi bi-arrow-right moving-arrow fs-3 heading_color"></i>
      </div>



        <!-- Outcome -->
        <div class="col-md-3 text-center p-4">
          <i class="bi bi-bar-chart-line-fill icon  heading_color fs-2 mb-3"></i>
          <h5 class="fw-bold heading_color">Outcome</h5>
          <p>Farmers maintain stable pond conditions under unpredictable weather.</p>
        </div>
      </div>
    </div>
  </div>
</section>
<br />
<section class="concept-flow py-5">
  <div class="container text-center position-relative">
    <h2 class="mb-5 fw-bold heading_color">How It Works / Concept Flow</h2>
   
    <div class="flow-line"></div>
    <div class="row g-4 justify-content-center">
      <!-- Step 1 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="Sensors continuously collect pond data like DO, pH, Temperature, Salinity, Ammonia, Turbidity"
        >
          <div class="icon-circle">
            <i class="bi bi-wifi"></i>
          </div>
          <h6>Capture</h6>
        </div>
      </div>

      <!-- Step 2 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="Data streams to the Digital Twin engine via IoT connectivity"
        >
          <div class="icon-circle">
            <i class="bi bi-cloud-arrow-up"></i>
          </div>
          <h6>Sync</h6>
        </div>
      </div>

      <!-- Step 3 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="The system renders a 3D virtual pond that mirrors real conditions"
        >
          <div class="icon-circle">
            <i class="bi bi-droplet-half"></i>
          </div>
          <h6>Visualize</h6>
        </div>
      </div>

      <!-- Step 4 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="AI models and simulations process data to predict water quality and disease risks"
        >
          <div class="icon-circle">
            <i class="bi bi-lightbulb"></i>
          </div>
          <h6>Analyze</h6>
        </div>
      </div>

      <!-- Step 5 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="Live guidance and alerts are sent through dashboard or mobile app"
        >
          <div class="icon-circle">
            <i class="bi bi-bell"></i>
          </div>
          <h6>Recommend</h6>
        </div>
      </div>

      <!-- Step 6 -->
      <div class="col-6 col-md-2">
        <div
          class="flow-step"
          data-bs-toggle="tooltip"
          title="Farmers take action; the Digital Twin refines predictions continuously"
        >
          <div class="icon-circle">
            <i class="bi bi-person-check"></i>
          </div>
          <h6>Act & Learn</h6>
        </div>
      </div>
      <div class="concept-image mb-5">
      <img
        src="assets/images/acqa.jpeg"
        alt="Concept Flow Diagram"
        class="img-fluid rounded shadow"
      />
    </div>
    </div>
  </div>
</section>



<script>
  const tooltipTriggerList = document.querySelectorAll(
    '[data-bs-toggle="tooltip"]'
  );
  tooltipTriggerList.forEach((el) => new bootstrap.Tooltip(el));
</script>
<section class="feature-timeline py-5">
  <div class="container">
    <div class="text-center mb-5">
      <h2 class="fw-bold heading_color">Our Product Features</h2>
      <p class="text-muted">
        Powerful capabilities designed to simplify pond management and boost
      productivity.
      </p>
    </div>

    <div class="timeline-two-col">
      <!-- Column 1 -->
      <div class="timeline-col">
        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-cpu-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">3D Pond Visualization</h5>
            <p>See every layer of the pond digitally, from surface to bottom
          sediment.</p>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-patch-check-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">Real-time Data Integration</h5>
            <p>Connects with water-quality, feed, and weather sensors.</p>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-lightbulb-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">AI Disease Predictor</h5>
            <p>Learns from data patterns to forecast WSSV and EMS risk.</p>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-recycle"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">Sustainability & Compliance Tracker </h5>
            <p>Tracks sustainability, compliance, and eco-friendly practices.</p>
          </div>
        </div>
      </div>

      <!-- Column 2 -->
      <div class="timeline-col">
        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-cloud-arrow-up-fill"></i></div>
          <div class="timeline-content">
           <h5 class="heading_color">Smart Decision Support</h5>
            <p>Suggests step-by-step actions based on live conditions.</p>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-bar-chart-line-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">Scenario Simulation Engine</h5>
            <p>Run virtual experiments on feeding, aeration, and weather impact.</p>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-grid-3x3-gap-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">Multi-Pond Dashboard</h5>
            <p>Manage and compare multiple ponds from one screen.</p>
          </div>
        </div>
         <div class="timeline-item">
          <div class="timeline-icon"><i class="bi bi-bell-fill"></i></div>
          <div class="timeline-content">
            <h5 class="heading_color">Alerts & Notifications</h5>
            <p>Mobile/web alerts for risk levels and actions.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>


<section class="container my-5">
  <h2 class="text-center fw-bold heading_color mb-4">
    Proven Results & Real-World Impact
  </h2>

  <div class="row g-4">
    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-basket3"></i>
        </div>
        <h5 class="heading_color fw-bold">Feed Optimization</h5>
        <p>
          Savings of <strong>15–25%</strong> through optimized feeding
          practices.
        </p>
      </div>
    </div>

    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-activity"></i>
        </div>
        <h5 class="heading_color fw-bold">Early Disease Detection</h5>
        <p>
          Alerts issued <strong>3–5 days</strong> before visible symptoms
          appear.
        </p>
      </div>
    </div>

    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-heart-pulse"></i>
        </div>
        <h5 class="heading_color fw-bold">Mortality Reduction</h5>
        <p>Water-quality-related mortality reduced by <strong>20%+</strong>.</p>
      </div>
    </div>

    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-droplet-half"></i>
        </div>
        <h5 class="heading_color fw-bold">Environmental Sustainability</h5>
        <p>Lower chemical usage and improved environmental impact.</p>
      </div>
    </div>

    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-bar-chart-line"></i>
        </div>
        <h5 class="heading_color fw-bold">Yield Stability</h5>
        <p>Consistent growth and stable yields across production cycles.</p>
      </div>
    </div>
    <div class="col-md-6 col-lg-4">
      <div class="result-card text-center p-4 h-100">
        <div class="icon-wrapper mb-3">
          <i class="bi bi-lightbulb-fill"></i>
        </div>
        <h5 class="heading_color fw-bold">Improved farmer decision efficiency </h5>
        <p>up to <strong>40%</strong> faster responses to critical pond events through AI-driven insights and mobile alerts.</p>
      </div>
    </div>
  </div>

  <p class="text-center mt-4 fw-bold">
    Delivering measurable ROI while promoting sustainable aquaculture practices.
  </p>
</section>

<section class="cta-section text-center py-5">
  <div class="container">
    <h2 class="mb-4 heading_color">Transform Your Aquaculture</h2>
    <p class="lead mb-4">
      Our <strong>Aquaculture Digital Twin</strong> brings your pond to life  a
      real-time 3D ecosystem that predicts, visualizes, and guides every
      decision. From disease prevention to feed optimization, it’s your farm’s
      digital brain  transforming data into action, and challenges into
      opportunity.
    </p>
    <a routerLink="/contact" href="/contact" class="btn btn-primary cta-btn">Request a Demo</a>
  </div>
</section>
